import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import SimpleAdmin, { DefaultOptions } from 'components/simpleAdmin';
import { apiDelete, apiGet, apiPatch, apiPost } from 'services';
import { setShowSuccess } from 'actions/ui';
import { adminLoading, handleError } from 'actions/admin';
import { CRM_Entities } from '../integrations/components/salesforce';
import { ArrayHelpers, Forms, Picker, SystemText } from 'e4shared';
import SortMetrics from './components/sortMetrics';
import { withRouter } from 'react-router-dom';
import { showpadAssets, showpadEvents } from 'data/integrations/showpad';

export const ExtraFieldPicker = ({ values, extraFields, onChange, readonly, isSalesforce, history }) => {
    const [filters, setFilters] = React.useState(values.filter_Fields || []);

    const showHelp = useSelector(state => state.ui.showFormHelp);

    const metrics = extraFields.filter(x => x.crmType === values.crm_Type);

    const changeFilter = (v, m) => {
        const current = [...filters];
        let metric = current.find(c => c.filter_Field === m.id);
        if (metric) {
            metric.filter_Field_Values = v;
        } else {
            current.push({ filter_Field: m.id, filter_Field_Values: v });
        }

        setFilters(current);
        onChange(current);
    };

    return (
        <div id="field-metric-picker" className="mb10">
            <div className="mb10">
                <label htmlFor="item-name">{SystemText('Admin/Metrics/Fields/ExtraFields/Label')}</label>
                {showHelp && (
                    <span className="form-control-help">
                        <span className="copy">{SystemText('Admin/Metrics/Fields/ExtraFields/Help')}</span>
                    </span>
                )}
            </div>
            {isSalesforce && (
                <div className="mb10">
                    <button
                        data-testid="button-salesforce"
                        className="btn btn-info btn-sm"
                        onClick={() => history.push('/salesforce-fields')}
                    >
                        {SystemText('Admin/Metrics/Fields/ExtraFields/Manage')}
                    </button>
                </div>
            )}
            <div className="form-stack">
                {ArrayHelpers.existsAndHasLength(metrics) ? (
                    metrics.map(m => {
                        const data = m.values.map(y => ({
                            name: y || SystemText('General/Controls/MetricPicker/NotSet'),
                            id: y,
                        }));
                        const currentValues = filters.find(f => f.filter_Field === m.id)?.filter_Field_Values;

                        return (
                            <div className={`flex flex-center mtm5 ${readonly && 'mb10'}`} key={`metric-${m.id}`}>
                                <div className="mr5 mb10" style={{ width: '50%' }}>
                                    <i className="fa fa-filter mr5" />
                                    {m.name}
                                </div>
                                <div className="mr5" style={{ width: '50%' }}>
                                    <Forms.Select
                                        data-testid="select-metric-picker-values"
                                        allowMultiple
                                        placeholder={SystemText('Admin/Metrics/Fields/ExtraFields/Placeholder')}
                                        inline
                                        data={data}
                                        readonly={readonly}
                                        value={currentValues}
                                        onChange={v => changeFilter(v, m)}
                                    />
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div>{SystemText('Admin/Metrics/Fields/ExtraFields/None')}</div>
                )}
            </div>
        </div>
    );
};

export const ValueRanges = ({ values, fieldProps, field, onChange, history, ...rest }) => {
    const [high, setHigh] = React.useState('');
    const [low, setLow] = React.useState('');

    React.useEffect(() => {
        const lowInt = low !== null && !isNaN(low) ? parseInt(low, 10) : -1;
        const highInt = high !== null && !isNaN(high) ? parseInt(high, 10) : -1;
        (low || high) && onChange([lowInt, highInt]);
    }, [high, low]);

    React.useEffect(() => {
        if (ArrayHelpers.existsAndHasLength(values.value_Range) && values.value_Range.length === 2) {
            setLow(values.value_Range[0] < 0 ? null : values.value_Range[0]);
            setHigh(values.value_Range[1] < 0 ? null : values.value_Range[1]);
        }
    }, [values.value_Range]);

    const readonly = !values.company_Id;

    return (
        <div className="form-group">
            <label>{rest.label}</label>
            {rest.help && <span className="form-control-help">{rest.help}</span>}
            <div className="flex flex-center">
                <input
                    data-testid="text-low"
                    type="number"
                    placeholder={rest.placeholder}
                    className="form-control"
                    value={low}
                    readOnly={readonly ? true : false}
                    onChange={e => setLow(e.target.value)}
                />
                <span className="pr10 pl10">{SystemText('Admin/Metrics/Fields/ValueRange/To')}</span>
                <input
                    data-testid="text-high"
                    type="number"
                    placeholder={rest.placeholder}
                    className="form-control"
                    value={high}
                    readOnly={readonly ? true : false}
                    onChange={e => setHigh(e.target.value)}
                />
            </div>
        </div>
    );
};

export const AdminMetrics = ({ history }) => {
    const dispatch = useDispatch();

    const [pickerItem, setPickerItem] = React.useState(null);
    const [data, setData] = React.useState(null);
    const [nonRatioFields, setNonRatioFields] = React.useState([]);
    const [extraFields, setExtraFields] = React.useState([]);
    const [extraFieldOptions, setExtraFieldOptions] = React.useState([]);
    const [entities, setEntities] = React.useState(CRM_Entities());
    const [competencies, setCompetencies] = React.useState(null);
    const [roles, setRoles] = React.useState([]);
    const [showSort, setShowSort] = React.useState(false);
    const [showPicker, setShowPicker] = React.useState(false);
    const [showRolePicker, setShowRolePicker] = React.useState(false);
    const [loaded, setLoaded] = React.useState(false);
    const user = useSelector(state => state.user.user);
    const loading = useSelector(state => state.admin.loading);
    const isSalesforce = useSelector(state => state.user.user.crmIntegration).toLowerCase() === 'salesforce';
    const isODBC = useSelector(state => state.user.user.integrations.some(i => i.ref === 'ODBC'));
    const isSFTP = useSelector(state => state.user.user.integrations.some(i => i.ref === 'SFTP'));
    const isShowpad = useSelector(state => state.user.user.integrations.some(i => i.ref === 'SHOWPAD'));
    const isSnowflake = useSelector(state => state.user.user.integrations.some(i => i.ref === 'SNOWFLAKE'));

    const text = {
        title: SystemText('Admin/Metrics/Title'),
        add: SystemText('Admin/Metrics/Add'),
        deleteWarning: SystemText('Admin/Metrics/DeleteWarning'),
        success: x => SystemText('Admin/Metrics/Success', x),
        successDefault: SystemText('Admin/Metrics/Success/Default'),
        setCompetencies: x => SystemText('Admin/Metrics/SetCompetencies', x),
        fields: {
            integration: {
                label: SystemText('Admin/Metrics/Fields/Integration/Label'),
            },
            system: {
                label: SystemText('Admin/Metrics/Fields/System/Label'),
            },
            displayName: {
                label: SystemText('Admin/Metrics/Fields/DisplayName/Label'),
                help: SystemText('Admin/Metrics/Fields/DisplayName/Help'),
            },
            description: {
                label: SystemText('Admin/Metrics/Fields/Description/Label'),
                help: SystemText('Admin/Metrics/Fields/Description/Help'),
            },
            legend: {
                label: SystemText('Admin/Metrics/Fields/Legend/Label'),
                help: SystemText('Admin/Metrics/Fields/Legend/Help'),
                placeholder: SystemText('Admin/Metrics/Fields/Legend/Placeholder'),
            },
            roles: {
                label: SystemText('Admin/Metrics/Fields/Roles/Label'),
                help: SystemText('Admin/Metrics/Fields/Roles/Help'),
                placeholder: SystemText('Admin/Metrics/Fields/Roles/Placeholder'),
            },
            competency: {
                label: SystemText('Admin/Metrics/Fields/Competency/Label'),
                help: SystemText('Admin/Metrics/Fields/Competency/Help'),
                placeholder: SystemText('Admin/Metrics/Fields/Competency/Placeholder'),
            },
            type: {
                label: SystemText('Admin/Metrics/Fields/Type/Label'),
                help: SystemText('Admin/Metrics/Fields/Type/Help'),
                yes: SystemText('Admin/Metrics/Fields/Type/Options/Yes'),
                no: SystemText('Admin/Metrics/Fields/Type/Options/No'),
            },
            ratio1: {
                label: SystemText('Admin/Metrics/Fields/Ratio1/Label'),
                help: SystemText('Admin/Metrics/Fields/Ratio1/Help'),
            },
            ratio2: {
                label: SystemText('Admin/Metrics/Fields/Ratio2/Label'),
                help: SystemText('Admin/Metrics/Fields/Ratio2/Help'),
            },
            entity: {
                label: SystemText('Admin/Metrics/Fields/Entity/Label'),
                help: SystemText('Admin/Metrics/Fields/Entity/Help'),
            },
            hilow: {
                label: SystemText('Admin/Metrics/Fields/HiLow/Label'),
                help: SystemText('Admin/Metrics/Fields/HiLow/Help'),
            },
            extra1: {
                label: SystemText('Admin/Metrics/Fields/Extra1/Label'),
                help: SystemText('Admin/Metrics/Fields/Extra1/Help'),
                placeholder: SystemText('Admin/Metrics/Fields/Extra1/Placeholder'),
            },
            extra2: {
                label: SystemText('Admin/Metrics/Fields/Extra2/Label'),
                help: SystemText('Admin/Metrics/Fields/Extra2/Help'),
                placeholder: SystemText('Admin/Metrics/Fields/Extra2/Placeholder'),
            },
            extra3: {
                label: SystemText('Admin/Metrics/Fields/Extra3/Label'),
                help: SystemText('Admin/Metrics/Fields/Extra3/Help'),
                placeholder: SystemText('Admin/Metrics/Fields/Extra3/Placeholder'),
            },
            format: {
                label: SystemText('Admin/Metrics/Fields/Format/Label'),
                help: SystemText('Admin/Metrics/Fields/Format/Help'),
            },
            sftpMetric: {
                label: SystemText('Admin/Metrics/Fields/SFTPMetric/Label'),
                help: SystemText('Admin/Metrics/Fields/SFTPMetric/Help'),
            },
            showpadEvent: {
                label: SystemText('Admin/Metrics/Fields/ShowpadEvent/Label'),
                help: SystemText('Admin/Metrics/Fields/ShowpadEvent/Help'),
            },
            showpadAsset: {
                label: SystemText('Admin/Metrics/Fields/ShowpadAsset/Label'),
                help: SystemText('Admin/Metrics/Fields/ShowpadAsset/Help'),
            },
            showpadValue: {
                label: SystemText('Admin/Metrics/Fields/ShowpadValue/Label'),
                help: SystemText('Admin/Metrics/Fields/ShowpadValue/Help'),
                yes: SystemText('Admin/Metrics/Fields/ShowpadValue/Yes'),
                no: SystemText('Admin/Metrics/Fields/ShowpadValue/No'),
            },
            snowflakeQuery: {
                label: SystemText('Admin/Metrics/Fields/SnowflakeQuery/Label'),
                help: SystemText('Admin/Metrics/Fields/SnowflakeQuery/Help'),
                placeholder: SystemText('Admin/Metrics/Fields/SnowflakeQuery/Placeholder'),
            },
            subType: {
                label: SystemText('Admin/Metrics/Fields/SubType/Label'),
                help: SystemText('Admin/Metrics/Fields/SubType/Help'),
                options: {
                    all: SystemText('Admin/Metrics/Fields/SubType/Options/All'),
                    calls: SystemText('Admin/Metrics/Fields/SubType/Options/Calls'),
                    emails: SystemText('Admin/Metrics/Fields/SubType/Options/Emails'),
                },
            },
            who: {
                label: SystemText('Admin/Metrics/Fields/Who/Label'),
                help: SystemText('Admin/Metrics/Fields/Who/Help'),
                helpTeam: SystemText('Admin/Metrics/Fields/Who/Help/Team'),
                options: {
                    creator: SystemText('Admin/Metrics/Fields/Who/Options/Creator'),
                    owner: SystemText('Admin/Metrics/Fields/Who/Options/Owner'),
                    team: SystemText('Admin/Metrics/Fields/Who/Options/Team'),
                },
            },
            showAs: {
                label: SystemText('Admin/Metrics/Fields/ShowAs/Label'),
                help: SystemText('Admin/Metrics/Fields/ShowAs/Help'),
                helpBasic: SystemText('Admin/Metrics/Fields/ShowAs/HelpBasic'),
                options: {
                    count: SystemText('Admin/Metrics/Fields/ShowAs/Options/Count'),
                    sum: SystemText('Admin/Metrics/Fields/ShowAs/Options/Sum'),
                    avg: SystemText('Admin/Metrics/Fields/ShowAs/Options/Average'),
                },
            },
            showAsUsing: {
                label: SystemText('Admin/Metrics/Fields/ShowAsUsing/Label'),
                help: SystemText('Admin/Metrics/Fields/ShowAsUsing/Help'),
            },
            groupByUsing: {
                label: SystemText('Admin/Metrics/Fields/GroupByUsing/Label'),
                help: SystemText('Admin/Metrics/Fields/GroupByUsing/Help'),
            },
            valueRange: {
                label: SystemText('Admin/Metrics/Fields/ValueRange/Label'),
                help: SystemText('Admin/Metrics/Fields/ValueRange/Help'),
                placeholder: SystemText('Admin/Metrics/Fields/ValueRange/Placeholder'),
            },
            filterBy: {
                label: SystemText('Admin/Metrics/Fields/FilterBy/Label'),
                help: SystemText('Admin/Metrics/Fields/FilterBy/Help'),
                options: {
                    all: SystemText('Admin/Metrics/Fields/FilterBy/Options/All'),
                    closedWon: SystemText('Admin/Metrics/Fields/FilterBy/Options/ClosedWon'),
                    closedLost: SystemText('Admin/Metrics/Fields/FilterBy/Options/ClosedLost'),
                    allClosed: SystemText('Admin/Metrics/Fields/FilterBy/Options/AllClosed'),
                },
            },
            extraField: {
                label: SystemText('Admin/Metrics/Fields/ExtraField/Label'),
                help: SystemText('Admin/Metrics/Fields/ExtraField/Help'),
            },
            extraValue: {
                label: SystemText('Admin/Metrics/Fields/ExtraValue/Label'),
                help: SystemText('Admin/Metrics/Fields/ExtraValue/Help'),
            },
        },
        table: {
            columns: {
                metric: SystemText('Admin/Metrics/Table/Columns/Metric'),
                type: SystemText('Admin/Metrics/Table/Columns/Type'),
                realtime: SystemText('Admin/Metrics/Table/Columns/Realtime'),
                roles: SystemText('Admin/Metrics/Table/Columns/Roles'),
                competencies: SystemText('Admin/Metrics/Table/Columns/Competencies'),
            },
        },
        pickers: {
            roles: {
                label: x => SystemText('Admin/Metrics/Pickers/Roles/Label', x),
                entity: SystemText('Admin/Metrics/Pickers/Roles/Entity'),
            },
        },
        buttons: {
            sort: SystemText('Admin/Metrics/Buttons/Sort'),
        },
    };

    React.useEffect(() => {
        apiGet('competency', user.guid, response => {
            setCompetencies(response.filter(c => c.active).map(c => ({ ...c, name: c.full_Name })));
        });

        apiGet('role', user.guid, response => setRoles(response));

        apiGet('crm-extra-fields/salesforce?ov=true', user.guid, response => {
            setExtraFields(response);
        });

        apiGet('crm-extra-fields/salesforce', user.guid, response => {
            setExtraFieldOptions(response);
        });

        apiGet('crm-entities', user.guid, response => {
            const allEntities = CRM_Entities()
                .concat(response.map(x => ({ ...x, entity: x.crm_Name })))
                .sort((a, b) => (a.name > b.name ? 1 : -1));
            setEntities(allEntities);
        });

        api.load();
    }, [user.guid]);

    const setLoading = value => dispatch(adminLoading(value));

    const sumExtraFields = (values, entity) => {
        const defaultOptions = values.crm_Type === 3 ? [{ id: 0, name: 'Amount' }] : [];
        const sumFields = defaultOptions
            .concat(extraFieldOptions.filter(f => f.extra_Sum && f.crmType === values?.crm_Type))
            .sort((a, b) => (a.name > b.name ? 1 : -1));
        return sumFields;
    };

    const dateExtraFields = (values, entity) => {
        const dateFields = [{ id: 0, name: 'Created' }]
            .concat(extraFieldOptions.filter(f => f.extra_Date && f.crmType === values?.crm_Type))
            .sort((a, b) => (a.name > b.name ? 1 : -1));
        return dateFields;
    };

    React.useEffect(() => {
        if (data && competencies) {
            setLoading(false);
            setLoaded(true);
        }
    }, [data, competencies]);

    const whoButtons = [
        { value: 'C', label: text.fields.who.options.creator },
        { value: 'O', label: text.fields.who.options.owner },
    ];

    extraFieldOptions
        .filter(f => f.extra_Email)
        .map(f => {
            whoButtons.push({
                value: `X-${f.id}`,
                label: f.name,
                show: values => values.crm_Type === f.crmType,
                fallback: 'C',
            });
        });

    const options = {
        ...DefaultOptions(),
        label: text.title,
        addNewLabel: text.add,
        showDelete: isSalesforce || isODBC || isSFTP || isShowpad || isSnowflake,
        cannotDeleteMessage: text.deleteWarning,
    };

    const fields = [
        {
            type: 'hidden',
            dataProperty: 'company_Id',
        },
        {
            type: 'hidden',
            dataProperty: 'ref',
        },
        {
            label: text.fields.integration.label,
            type: 'label',
            dataProperty: 'integration',
        },
        {
            label: text.fields.system.label,
            type: 'label',
            dataProperty: 'system_Name',
            toggleProperty: 'id',
        },
        {
            label: text.fields.displayName.label,
            help: text.fields.displayName.help,
            type: 'text',
            mandatory: true,
            dataProperty: 'name',
            maxLength: 100,
            generalErrors: true,
            uniqueErrors: true,
        },
        {
            label: text.fields.description.label,
            help: text.fields.description.help,
            type: 'textarea',
            dataProperty: 'description',
            rows: 5,
        },
        {
            label: text.fields.legend.label,
            help: text.fields.legend.help,
            placeholder: text.fields.legend.placeholder,
            type: 'text',
            maxLength: 15,
            mandatory: true,
            dataProperty: 'legend',
        },
        {
            label: text.fields.competency.label,
            help: text.fields.competency.help,
            placeholder: text.fields.competency.placeholder,
            type: 'select',
            data: competencies,
            allowMultiple: true,
            dataProperty: 'competency_Ids',
        },
        {
            label: text.fields.roles.label,
            help: text.fields.roles.help,
            placeholder: text.fields.roles.placeholder,
            type: 'select',
            data: roles,
            allowMultiple: true,
            dataProperty: 'role_Ids',
        },
        {
            label: text.fields.type.label,
            help: text.fields.type.help,
            type: 'toggle',
            yesLabel: text.fields.type.yes,
            noLabel: text.fields.type.no,
            dataProperty: 'show_Ratios',
            toggleProperty: 'integration',
            toggleValue: 'CRM',
            readonly: (v, e) => !e.company_Id,
        },
        {
            label: text.fields.ratio1.label,
            help: text.fields.ratio1.help,
            type: 'select',
            data: nonRatioFields,
            dataProperty: 'ratio_Id1',
            dataBlock: 'id',
            mandatory: true,
            toggleProperty: 'show_Ratios',
            readonly: (v, e) => !e.company_Id,
        },
        {
            label: text.fields.ratio2.label,
            help: text.fields.ratio2.help,
            type: 'select',
            data: nonRatioFields,
            dataProperty: 'ratio_Id2',
            dataBlock: 'id',
            mandatory: true,
            toggleProperty: 'show_Ratios',
            readonly: (v, e) => !e.company_Id,
        },
        {
            label: text.fields.entity.label,
            help: text.fields.entity.help,
            type: 'select',
            data: entities,
            dataProperty: 'crm_Type',
            toggleProperty: 'integration',
            toggleValue: 'CRM',
            hideField: (v, e) => v.show_Ratios || e?.realTime,
            mandatory: true,
            readonly: (v, e) => !e.company_Id,
        },
        {
            label: text.fields.subType.label,
            help: text.fields.subType.help,
            type: 'buttons',
            buttons: [
                { value: '', label: text.fields.subType.options.all },
                { value: 'call', label: text.fields.subType.options.calls },
                { value: 'email', label: text.fields.subType.options.emails },
            ],
            dataProperty: 'filter_Type',
            toggleProperty: 'crm_Type',
            toggleValue: 2,
            hideField: (v, e) => v.show_Ratios,
            readonly: (v, e) => !e.company_Id,
        },
        {
            label: text.fields.who.label,
            help: text.fields.who.help,
            type: 'buttons',
            buttons: whoButtons,
            dataProperty: 'filter_Who',
            toggleProperty: 'crm_Type',
            hideField: (v, e) => v.show_Ratios,
            readonly: (v, e) => !e.company_Id,
        },
        {
            label: text.fields.showAs.label,
            help: text.fields.showAs.help,
            type: 'buttons',
            buttons: [
                { value: 'C', label: text.fields.showAs.options.count },
                { value: 'S', label: text.fields.showAs.options.sum },
                { value: 'A', label: text.fields.showAs.options.avg },
            ],
            toggleProperty: 'integration',
            toggleValue: 'CRM',
            dataProperty: 'value_Type',
            readonly: (v, e) => !e.company_Id,
        },
        {
            label: text.fields.groupByUsing.label,
            help: text.fields.groupByUsing.help,
            type: 'select',
            dataProperty: 'date_Field',
            data: dateExtraFields,
            toggleProperty: 'integration',
            toggleValue: 'CRM',
            readonly: (v, e) => !e.company_Id,
            inTitleBar: true,
        },
        {
            label: text.fields.showAsUsing.label,
            help: text.fields.showAsUsing.help,
            type: 'select',
            dataProperty: 'calc_Field',
            data: sumExtraFields,
            mandatory: true,
            toggleProperty: 'value_Type',
            toggleValue: ['A', 'S'],
            readonly: (v, e) => !e.company_Id,
            inTitleBar: true,
        },
        {
            label: text.fields.valueRange.label,
            help: text.fields.valueRange.help,
            placeholder: text.fields.valueRange.placeholder,
            type: 'special',
            dataProperty: 'value_Range',
            toggleProperty: 'value_Type',
            toggleValue: ['A', 'S'],
            readonly: (v, e) => !e.company_Id,
            renderer: (values, fieldProps, field) => <ValueRanges values={values} {...fieldProps} {...field} />,
            inTitleBar: true,
        },
        {
            label: text.fields.showAs.label,
            help: text.fields.showAs.helpBasic,
            type: 'buttons',
            buttons: [
                { value: 'C', label: text.fields.showAs.options.count },
                { value: 'S', label: text.fields.showAs.options.sum },
            ],
            dataProperty: 'value_Type',
            toggleProperty: 'ref',
            toggleValue: 'ODBC',
            readonly: (v, e) => !e.company_Id,
        },
        {
            label: text.fields.filterBy.label,
            help: text.fields.filterBy.help,
            type: 'buttons',
            buttons: [
                { value: '', label: text.fields.filterBy.options.all },
                { value: 'W', label: text.fields.filterBy.options.closedWon },
                { value: 'L', label: text.fields.filterBy.options.closedLost },
                { value: 'C', label: text.fields.filterBy.options.allClosed },
            ],
            dataProperty: 'filter_Stage',
            toggleProperty: 'crm_Type',
            toggleValue: 3,
            readonly: (v, e) => !e.company_Id,
        },
        {
            label: text.fields.extraValue.label,
            help: text.fields.extraValue.help,
            type: 'special',
            renderer: (values, fieldProps) => (
                <ExtraFieldPicker
                    values={values}
                    history={history}
                    extraFields={extraFields}
                    isSalesforce={isSalesforce}
                    {...fieldProps}
                />
            ),
            data: (v, e) =>
                v ? (extraFields || []).find(x => x.field_Id === v.filter_Field)?.values.map(y => ({ name: y, id: y })) : [],
            dataProperty: 'filter_Fields',
            toggleProperty: 'crm_Type',
            readonly: (v, e) => !e.company_Id,
        },
        {
            label: text.fields.extra1.label,
            type: 'tags',
            dataProperty: 'extra1',
            toggleProperty: 'ref',
            toggleValue: 'ODBC',
            help: text.fields.extra1.help,
        },
        {
            label: text.fields.extra2.label,
            type: 'tags',
            dataProperty: 'extra2',
            toggleProperty: 'ref',
            toggleValue: 'ODBC',
            help: text.fields.extra2.help,
        },
        {
            label: text.fields.hilow.label,
            help: text.fields.hilow.help,
            type: 'toggle',
            dataProperty: 'low_Good',
            readonly: (v, e) => !e.company_Id,
            inTitleBar: true,
        },
        {
            label: text.fields.extra3.label,
            type: 'tags',
            dataProperty: 'extra3',
            toggleProperty: 'ref',
            toggleValue: 'ODBC',
            help: text.fields.extra3.help,
        },
        {
            label: text.fields.sftpMetric.label,
            type: 'text',
            dataProperty: 'property',
            maxLength: 100,
            toggleProperty: 'ref',
            toggleValue: 'SFTP',
            mandatory: true,
            help: text.fields.sftpMetric.help,
        },
        {
            label: text.fields.showpadEvent.label,
            type: 'select',
            dataProperty: 'extra4',
            data: showpadEvents,
            allowMultiple: false,
            toggleProperty: 'ref',
            toggleValue: 'SHOWPAD',
            help: text.fields.showpadEvent.help,
        },
        {
            label: text.fields.showpadAsset.label,
            type: 'select',
            dataProperty: 'extra1',
            data: showpadAssets,
            allowMultiple: true,
            toggleProperty: 'ref',
            toggleValue: 'SHOWPAD',
            help: text.fields.showpadAsset.help,
        },
        {
            label: text.fields.showpadValue.label,
            type: 'toggle',
            dataProperty: 'calc_Sum',
            noLabel: text.fields.showpadValue.no,
            yesLabel: text.fields.showpadValue.yes,
            toggleProperty: 'ref',
            toggleValue: 'SHOWPAD',
            help: text.fields.showpadValue.help,
        },
        {
            label: text.fields.snowflakeQuery.label,
            type: 'textarea',
            rows: 4,
            dataProperty: 'extra',
            toggleProperty: 'ref',
            toggleValue: 'SNOWFLAKE',
            placeholder: text.fields.snowflakeQuery.placeholder,
            help: text.fields.snowflakeQuery.help,
            mandatory: true,
            //SELECT SALESMANEMAILADDRESS, MONTHDATE, NETSALES FROM SALESMANSALES WHERE RIMDIAMETER = 16
        },
        {
            label: text.fields.format.label,
            help: text.fields.format.help,
            type: 'select',
            dataProperty: 'format',
            data: [
                { id: 'Q', name: SystemText('Admin/Metrics/Fields/Format/Data/Q') },
                { id: 'D', name: SystemText('Admin/Metrics/Fields/Format/Data/D') },
                { id: 'M', name: SystemText('Admin/Metrics/Fields/Format/Data/M') },
                { id: 'P', name: SystemText('Admin/Metrics/Fields/Format/Data/P') },
                { id: 'S', name: SystemText('Admin/Metrics/Fields/Format/Data/S') },
            ],
            readonly: (v, e) => !e.company_Id,
            mandatory: true,
            inTitleBar: true,
        },
    ];

    const tableColumns = [
        { label: text.table.columns.metric, dataProperty: 'name' },
        { label: text.table.columns.type, dataProperty: 'integration' },
        { label: text.table.columns.realtime, dataProperty: 'realTime', toggle: true, className: 'visible-lg' },
        {
            label: text.table.columns.roles,
            dataProperty: 'role_Ids',
            showCount: true,
            onClick: item => {
                setPickerItem(item);
                setShowRolePicker(true);
            },
        },
        {
            label: text.table.columns.competencies,
            dataProperty: 'competency_Ids',
            showCount: true,
            onClick: item => {
                setPickerItem(item);
                setShowPicker(true);
            },
        },
    ];

    const defaultValues = {
        company_Id: user.company_Id,
        crm_Type: 3,
        ref: 'CRM',
        integration: 'CRM',
        value_Type: 'C',
        filter_Type: '',
        filter_Stage: '',
        filter_Who: 'C',
        active: true,
        high_Good: true,
        format: 'Q',
        calc_Field: 0,
        date_Field: 0,
    };

    const api = {
        load: () => {
            setLoading(true);
            apiGet('all-fields', user.guid, response => {
                const sorted = response
                    .filter(
                        x =>
                            (x.integration === 'CRM' && user.crmIntegration) ||
                            user.integrations?.some(i => i.ref === x.ref || i.ref === x.integration)
                    )
                    .sort((a, b) => (a.name > b.name ? 1 : -1))
                    .map(x => ({
                        ...x,
                        sort_Field: x.sort_Field || 0,
                        calc_Field: x.calc_Field || 0,
                        cannotDelete: !x.company_Id,
                    }));
                setData(sorted);

                setNonRatioFields(sorted.filter(s => s.integration === 'CRM' && !s.ratio_Id1));
            });
        },
        delete: (ids, callback, data) => {
            if (!Array.isArray(ids)) ids = [ids];
            if (!Array.isArray(data)) data = [data];
            ids.forEach((id, idx) => {
                apiDelete(`metric/${id}/${data.find(x => x.id === id).ref}`, null, user.guid, () => {
                    if (idx + 1 === ids.length) {
                        api.load();
                        callback?.();
                    }
                });
            });
        },
        add: (metric, callback, errorCallback) => {
            metric.value_Type_Setter = metric.value_Type;
            metric.filter_Stage_Setter = metric.filter_Stage;

            apiPost(
                'metric',
                metric,
                user.guid,
                response => {
                    if (response.error) {
                        errorCallback?.({ property: 'extra', value: response.error });
                    } else {
                        dispatch(setShowSuccess(text.success(text.successDefault)));
                        setShowPicker(false);
                        api.load();
                        callback?.();
                    }
                },
                error => {
                    handleError(dispatch, error);
                },
                'POST',
                'json'
            );
        },
        update: (metric, callback, errorCallback) => {
            metric.ratio_Id1 = metric.show_Ratios ? metric.ratio_Id1 : 0;
            metric.ratio_Id2 = metric.show_Ratios ? metric.ratio_Id2 : 0;
            metric.update_Competency_Ids = metric.update_Competency_Ids || metric.competency_Ids;

            metric.value_Type_Setter = metric.value_Type;
            metric.filter_Stage_Setter = metric.filter_Stage;

            apiPatch(
                'metric',
                metric,
                user.guid,
                response => {
                    if (response.error) {
                        errorCallback?.({ property: 'extra', value: response.error });
                    } else {
                        dispatch(setShowSuccess(text.success(text.successDefault)));
                        setShowPicker(false);
                        callback?.();
                    }
                },
                error => {
                    handleError(dispatch, error);
                },
                'PATCH',
                'json'
            );
        },
    };

    const extraAddButtons = [];

    if (isODBC) {
        extraAddButtons.push({
            label: SystemText('Admin/Metrics/Buttons/Metric', user.integrations.find(i => i.ref === 'ODBC').extra),
            override: { company_Id: user.company_Id, integration: 'Custom DB', ref: 'ODBC' },
        });
    }

    if (isSFTP) {
        extraAddButtons.push({
            label: SystemText('Admin/Metrics/Buttons/Metric', 'SFTP'),
            override: { company_Id: user.company_Id, integration: 'SFTP', ref: 'SFTP' },
        });
    }

    if (isShowpad) {
        extraAddButtons.push({
            label: SystemText('Admin/Metrics/Buttons/Metric', 'Showpad'),
            override: { company_Id: user.company_Id, integration: 'Showpad', ref: 'SHOWPAD' },
        });
    }

    if (isSnowflake) {
        extraAddButtons.push({
            label: SystemText('Admin/Metrics/Buttons/Metric', 'Snowflake'),
            override: { company_Id: user.company_Id, integration: 'Snowflake', ref: 'SNOWFLAKE' },
        });
    }

    return (
        <div>
            <SimpleAdmin
                data={loading || !loaded ? [] : data}
                loading={loading}
                fields={fields}
                blockAddNew={!isSalesforce}
                options={options}
                columns={tableColumns}
                defaultValues={defaultValues}
                update={api.update}
                add={api.add}
                load={api.load}
                remove={(value, callback, data) => api.delete(value, callback, data)}
                extraAddButtons={extraAddButtons.sort((a, b) => (a.label > b.label ? 1 : -1))}
                extraButtons={
                    <button className="btn btn-primary" data-testid="button-sort-metrics" onClick={() => setShowSort(true)}>
                        <i className="fa fa-sort-amount-asc mr10" />
                        {text.buttons.sort}
                    </button>
                }
            />
            <Picker
                data-testid="competency-picker"
                title={text.setCompetencies(pickerItem?.name)}
                entity="Competencies"
                data={competencies}
                nameProperty="admin_Full_Name"
                value={pickerItem?.competency_Ids}
                show={showPicker}
                onSave={ids => {
                    api.update({ ...pickerItem, update_Competency_Ids: ids }, () => api.load());
                }}
                onClose={() => {
                    setPickerItem(null);
                    setShowPicker(false);
                }}
                itemPrefix="star"
            />
            <Picker
                title={text.pickers.roles.label(pickerItem?.name)}
                entity={text.pickers.roles.entity}
                data={roles}
                value={pickerItem?.role_Ids}
                show={showRolePicker}
                onSave={ids => {
                    api.update({ ...pickerItem, role_Ids: ids }, () => {
                        setPickerItem(null);
                        setShowRolePicker(false);
                        api.load();
                    });
                }}
                onClose={() => {
                    setPickerItem(null);
                    setShowRolePicker(false);
                    dispatch(setShowSuccess(false));
                }}
                itemPrefix="street-view"
            />
            <SortMetrics show={showSort} onClose={() => setShowSort(false)} />
        </div>
    );
};

export default withRouter(AdminMetrics);
